import React from 'react'

import { safeHtml } from '@library/dom'

import {
  AlignItems,
  Box,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { ValueBannerContentBlock } from '@library/api-menu-experience'

const HIGHLIGHT_COLOR_VARIABLE = '--basket-value-banner-highlight-color'

// The offer tag, styled the same as in the menu banner
const Message = withCitrusTheme(Text, () => ({
  '& .highlight': {
    border: `1px solid var(${HIGHLIGHT_COLOR_VARIABLE})`,
    borderRadius: '4px',
    color: `var(${HIGHLIGHT_COLOR_VARIABLE})`,
    fontWeight: FontWeight.Bold,
    padding: '0 4px',
  },
}))

// The API only sends `<span class="highlight">`, so nothing else is allowed through

type BasketValueBannerProps = {
  contentBlock: Pick<
    ValueBannerContentBlock,
    'descriptionHTML' | 'color' | 'backgroundColor' | 'highlightColor' | 'supportingText'
  >
}

// The applied-offer card at the top of the basket. It is the same `value-banner` block as on the menu,
// so it shows whatever the API sends: the offer tag in the description and the supporting text. It uses
// the block's own colours, and webclient has no dark theme so only the light values are used. The card
// look (rounded, no bottom border, no outer margin) is specific to the basket.
export const BasketValueBanner = ({ contentBlock }: BasketValueBannerProps) => {
  const { descriptionHTML, color, backgroundColor, highlightColor, supportingText } = contentBlock
  const highlight = (highlightColor ?? color).light

  return (
    <Box
      alignItems={AlignItems.Center}
      data-testid="basket-value-banner"
      display={Display.Flex}
      gap={2}
      paddingH={4}
      paddingV={3}
      style={
        {
          [HIGHLIGHT_COLOR_VARIABLE]: highlight,
          backgroundColor: backgroundColor.light,
          borderRadius: '8px',
          color: color.light,
        } as React.CSSProperties
      }
    >
      <Box
        data-testid="basket-value-banner-icon"
        display={Display.Flex}
        style={{ color: highlight }}
      >
        <Icon name="offer_pound" size={8} variant={IconVariant.Inherit} />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
        <Message
          fontFamily={FontFamily.Bold}
          fontWeight={FontWeight.Bold}
          size={4}
          style={{ color: color.light }}
          dangerouslySetInnerHTML={safeHtml(descriptionHTML)}
        />
        {supportingText && (
          <Text
            data-testid="basket-value-banner-supporting-text"
            size={2}
            style={{ color: color.light }}
          >
            {supportingText}
          </Text>
        )}
      </Box>
    </Box>
  )
}
